<script setup lang="tsx">
import type BottomSheetScaffold from '~/components/BottomSheetScaffold/BottomSheetScaffold.vue';
import type { OrdenationDropdownOption } from '~/components/Filters/OrdenationSelect';
import type PropertyListSection from '~/components/PropertyList/PropertyListSection.vue';
import type { RegionInfoLinkClickedEmitProps } from '~/components/RegionInfo';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { SheetSnapHeights } from '~/composables/useBottomSheet';
import type { ListingTrackingContext } from '~/composables/useListingEventTracking';
import type { DrawingMode } from '~/composables/useMapDrawing';
import type { PropertiesListingMapState } from '~/composables/useMapListing';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { BaseClusterData } from '~/core/service/googleMaps/components/GmapClusterMarker';
// Import de TIPO de proposito: import estatico destes .vue anulava o
// defineAsyncComponent do plugin e punha a familia do mapa no chunk da pagina.
import type GmapClusterMarkerComponent from '~/core/service/googleMaps/components/GmapClusterMarker/GmapClusterMarker.vue';
import type GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import type { H3ClusterId } from '~~/core/ports/repositories';
import type { BreadcrumbList, WithContext } from 'schema-dts';

import { until, useWindowSize } from '@vueuse/core';
import { useEventDataFilters } from '~/composables/events/useEventDataFilters/useEventDataFilters';
import { useEvents } from '~/composables/events/useEvents';
import { usePropertiesListingJsonLd } from '~/composables/jsonLd/usePropertiesListingJsonLd';
import { setupLastSrpUrlActivityListeners } from '~/composables/lastSrpUrl/activityListeners';
import { captureLastSrpUrlFromRoute } from '~/composables/lastSrpUrl/utils';
import { useShortsSrpVariant } from '~/composables/shorts/useShortsSrpVariant';
import { useAgentFilterDraftPreservation } from '~/composables/useAgentFilterDraftPreservation';
import { SheetLevel } from '~/composables/useBottomSheet';
import {
	FILTER_BAR_HEIGHT,
	HEADER_OFFSET_CONFIG,
	MOBILE_CHROME_HEIGHT
} from '~/composables/useDragToBottom/constants';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { DEFAULT_FILTER } from '~/composables/useListing/filter';
import { useManager } from '~/composables/useListing/manager';
import { DEFAULT_SERVER_PAGINATION } from '~/composables/useListing/pagination';
import { buildManagedListingTrackingContext } from '~/composables/useListingEventTracking';
import { useListingInfoLabel } from '~/composables/useListingInfoLabel/useListingInfoLabel';
import { useMapClusters } from '~/composables/useMapClusters/useMapClusters';
import {
	MapState,
	useMapController
} from '~/composables/useMapController/useMapController';
import { useMapDrawing } from '~/composables/useMapDrawing';
import {
	createMapDragEndGuard,
	createMapDrawingModeCoordinator,
	createMapDrawingResetGuard
} from '~/composables/useMapDrawing/modeCoordinator';
import { initializeMapState } from '~/composables/useMapListing';
import { usePropertiesListingHeader } from '~/composables/usePropertiesListingHeader';
import { usePropertyResponsiveFilters } from '~/composables/usePropertyResponsiveFilters/usePropertyResponsiveFilters';
import { useSelectedAgentSummary } from '~/composables/useSelectedAgentSummary';
import { useSeoTagsListing } from '~/composables/useSeoTagsListing/useSeoTagsListing';
import { useSingleFieldSuggest } from '~/composables/useSingleFieldSuggest';
import { useWindowScrolling } from '~/composables/useWindowScrolling';
import {
	PropertyTypePlurals,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cleanLocationQueryFromPath } from '~/utils/cleanLocationQueryFromPath';
import {
	createH3SelectionUrlSyncQueue,
	getNormalizedH3IdsKey
} from '~/utils/h3SelectionUrlSync';
import {
	dehyphenateString,
	getChangedKeysFilters,
	grammarInflection,
	screenSizesInPixels,
	slugify
} from '~~/utils';
import { joinURL } from 'ufo';

import { MapDrawingButton } from '~/components/Buttons/MapDrawingButton';
import MobileScrollToMapButton from '~/components/Buttons/MobileScrollToMapButton.vue';
import { provideCarouselScrolling } from '~/components/Card/CardCarousel/useCarouselScrolling';

import IconMaterialSymbolsLightChevronLeft from '~icons/material-symbols-light/chevron-left';

const { searchPropertyEvents, trackFilterApplied } = useEvents();
const { getFilterAppliedValues } = useEventDataFilters();
const { isShortsSrpVariant } = useShortsSrpVariant();
const { ft_srp_agent_filter: isAgentFilterEnabled } = useFeatureFlags();

const shortsDisplayMode = computed(() =>
	isShortsSrpVariant.value ? 'every-six' : 'first-fold'
);

definePageMeta({
	name: 'PropertiesListing',
	layout: 'properties-listing',
	pageTitle: 'Imóveis',
	key: 'properties-listing-page',
	middleware: ['srp-agent-filter'],
	alias: ['/venda/imoveis', '/aluguel/imoveis']
});

const route = useRoute();
const router = useRouter();

const mapState = useState<PropertiesListingMapState>(
	'properties-listing-map',
	initializeMapState
);

const GmapClusterMarker = defineAsyncComponent(
	() =>
		import('~/core/service/googleMaps/components/GmapClusterMarker/GmapClusterMarker.vue')
);

const gmapRef = ref<InstanceType<typeof GmapMap> | null>(null);
const clusterMarkersRefs = ref<
	Map<string, InstanceType<typeof GmapClusterMarkerComponent>>
>(new Map());

const mapOptions = computed(() => {
	return {
		gestureHandling: 'greedy',
		draggable: true,
		scrollwheel: true,
		disableDefaultUI: false,
		zoomControl: true
	};
});

// `google.maps.ControlPosition` só existe depois que o SDK carrega. Lê-lo dentro
// de `mapOptions` (computed sem deps reativas) cachearia `undefined` quando o
// template avalia antes do load, e o GmapMap não re-aplica `zoomControlOptions`.
// Aplicamos a posição de forma imperativa assim que a instância do mapa existe
// (o que garante o SDK já carregado).
watch(
	() => gmapRef.value?.mapInstance,
	mapInstance => {
		const controlPosition = globalThis.google?.maps?.ControlPosition;
		if (!mapInstance || !controlPosition) return;
		mapInstance.setOptions({
			zoomControl: true,
			zoomControlOptions: { position: controlPosition.RIGHT_BOTTOM }
		});
	}
);

// NOTA: useAddresses() era chamado aqui para pré-carregar dados,
// mas como RegionSelect está dentro de ClientOnly, não é necessário
// chamar no servidor. Os componentes que usam useAddresses() estão
// dentro de <ClientOnly> e farão a chamada quando necessário.

const propertyListSectionRef = useTemplateRef<InstanceType<
	typeof PropertyListSection
> | null>('propertyListSectionRef');

const initialFilters = computed<Partial<PropertyFilters>>(() =>
	PropertyFiltersPresenter.getFiltersFromPath(
		route.fullPath,
		{ ...DEFAULT_FILTER },
		'imoveis'
	)
);

const {
	filters,
	isFetching,
	properties,
	hasProperties,
	hasError,
	filteredCount,
	displayPagination,
	hasLessThenTenProperties,
	isFetchingForTheFirstTime,
	nextPage,
	updateFiltersManager,
	clearFilters
} = useManager({
	page: 'imoveis',
	initialFilters,
	includeH3Clusters: true,
	config: {
		shouldScrollToTopOnFiltersChange: true,
		scrollElement: propertyListSectionRef
	}
});

const isSidebarActive = shallowRef<boolean>(false);
const { preserveSidebarDrafts, preserveDraftsForAgentRemoval } =
	useAgentFilterDraftPreservation(isSidebarActive);
const {
	selectedAgent,
	isFetchingSelectedAgent,
	selectedAgentResolution,
	retrySelectedAgentSummary
} = useSelectedAgentSummary({
	isEnabled: isAgentFilterEnabled,
	agentSlug: () => filters.value.agentSlug,
	onAgentNotFound: preserveDraftsForAgentRemoval
});

const trackingContext = computed<ListingTrackingContext>(() =>
	buildManagedListingTrackingContext({
		placement: 'listagem',
		filters: filters.value,
		filteredCount: filteredCount.value,
		page: displayPagination.value.page,
		perPage: displayPagination.value.perPage
	})
);

// No SSR, aguarda a primeira requisição terminar antes de renderizar
if (import.meta.server) {
	await until(() => !isFetchingForTheFirstTime.value).toBeTruthy();
}

// Sync macroregion with page filters
// This automatically keeps the macroregion in sync when filters change
useSingleFieldSuggest({ filters });

const { updateSeoMeta, pageTitle, pageDescription } = useSeoTagsListing(
	filters,
	filteredCount,
	properties
);

updateSeoMeta();

// Only noindex on genuine zero results, never on a failed fetch — otherwise a
// transient failure would drop an otherwise-valid page instead of letting Google retry.
const shouldAddNoindex = computed<boolean>(
	() =>
		!isFetchingForTheFirstTime.value && !hasProperties.value && !hasError.value
);

useHead({
	meta: computed(() =>
		shouldAddNoindex.value
			? [
					{
						name: 'robots',
						content: 'noindex'
					}
				]
			: []
	)
});

const {
	trackMapListingClicked,
	trackBreadcrumbClicked,
	trackCtaViewMoreClicked,
	trackReorderClicked,
	trackPageView,
	trackEmptyState,
	trackLinkClicked,
	trackMapListingInteracted,
	trackSelectionCleared,
	trackBoundariesCleared
} = useEvents();

const { filterComponents, numberOfAppliedClosedFilters, appliedFilterKeys } =
	usePropertyResponsiveFilters({
		filters: computed(() => filters.value),
		updateFilters: updateFiltersManager,
		pageName: 'imoveis',
		selectedAgent,
		selectedAgentResolution,
		retrySelectedAgent: retrySelectedAgentSummary
	});

function updateOrdenation(event: {
	sortBy: PropertyFilters['sortBy'];
	orderBy: PropertyFilters['orderBy'];
}) {
	router.push(
		PropertyFiltersPresenter.getPathFromFilters(
			{
				...filters.value,
				sortBy: event.sortBy,
				orderBy: event.orderBy
			},
			{},
			{ page: 'imoveis' }
		)
	);
}

async function waitForFilteredCount() {
	return await until(() => typeof filteredCount.value === 'number').toBe(true);
}

async function waitForFetchingToComplete() {
	return await until(() => !isFetching.value).toBe(true);
}

function getLocationChangeType(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): 'addition' | 'removal' | 'substitution' | 'none' {
	const getArrayLength = (arr: any[] | undefined) => arr?.length || 0;

	const current = {
		regions: getArrayLength(currentFilters.regions),
		cities: getArrayLength(currentFilters.cities),
		macroregions: getArrayLength(currentFilters.macroregions),
		hasAddress: !!currentFilters.addressSlug,
		hasPoi: !!(currentFilters.poiLocationLat && currentFilters.poiLocationLng)
	};

	const updated = {
		regions: getArrayLength(newFilters.regions),
		cities: getArrayLength(newFilters.cities),
		macroregions: getArrayLength(newFilters.macroregions),
		hasAddress: !!newFilters.addressSlug,
		hasPoi: !!(newFilters.poiLocationLat && newFilters.poiLocationLng)
	};

	const hasLocationSubstitution =
		(newFilters.regions?.[0] !== currentFilters.regions?.[0] &&
			!!newFilters.regions?.[0]) ||
		(newFilters.cities?.[0] !== currentFilters.cities?.[0] &&
			!!newFilters.cities?.[0]) ||
		(newFilters.states?.[0] !== currentFilters.states?.[0] &&
			!!newFilters.states?.[0]) ||
		(newFilters.macroregions?.[0] !== currentFilters.macroregions?.[0] &&
			!!newFilters.macroregions?.[0]) ||
		(newFilters.addressSlug !== currentFilters.addressSlug &&
			updated.hasAddress) ||
		((newFilters.poiLocationLat !== currentFilters.poiLocationLat ||
			newFilters.poiLocationLng !== currentFilters.poiLocationLng) &&
			updated.hasPoi);

	if (hasLocationSubstitution) return 'substitution';

	const hasLocationAddition =
		updated.regions > current.regions ||
		updated.cities > current.cities ||
		updated.macroregions > current.macroregions ||
		(!current.hasAddress && updated.hasAddress) ||
		(!current.hasPoi && updated.hasPoi);

	if (hasLocationAddition) return 'addition';

	const hasLocationRemoval =
		updated.regions < current.regions ||
		updated.cities < current.cities ||
		updated.macroregions < current.macroregions ||
		(current.hasAddress && !updated.hasAddress) ||
		(current.hasPoi && !updated.hasPoi);

	if (hasLocationRemoval) return 'removal';

	return 'none';
}

function createBaseFilters(
	newFilters: PropertyFilters,
	locationChangeType: 'addition' | 'removal' | 'substitution' | 'none'
): PropertyFilters {
	const shouldPreserveBounds = locationChangeType === 'none';
	const hasLocationChange = locationChangeType !== 'none';

	let bounds = undefined;
	let zoom = undefined;
	let h3Ids = newFilters.h3Ids;

	if (shouldPreserveBounds) {
		const currentBounds = gmapRef.value?.mapInstance?.getBounds();
		const currentZoom = gmapRef.value?.mapInstance?.getZoom();

		bounds = currentBounds
			? getBoundsString(currentBounds)
			: filters.value.bounds;
		zoom = currentZoom || filters.value.zoom;
	}

	if (hasLocationChange) {
		h3Ids = undefined;
	}

	return { ...newFilters, bounds, zoom, h3Ids } as PropertyFilters;
}

type LocationChangeType = 'addition' | 'removal' | 'substitution' | 'none';

function updateClustersAndProperties(
	clusterFilters: PropertyFilters,
	propertyFilters: PropertyFilters,
	locationChangeType: LocationChangeType
): void {
	updateClustersForLocationChange(clusterFilters, locationChangeType);
	updateUrlAndListing(propertyFilters);
}

function updateClustersForLocationChange(
	newFilters: PropertyFilters,
	changeType: LocationChangeType
): void {
	const isLocationChange = changeType !== 'none';
	if (isLocationChange) {
		const filtersData = {
			...newFilters,
			bounds: undefined,
			zoom: undefined,
			h3Ids: undefined
		};
		mapController.processLocationFiltersChange('filters-bar');
		mapController.forceUpdateClusters(filtersData as PropertyFilters);
		return;
	}
	const currentBounds = gmapRef.value?.mapInstance?.getBounds();
	const currentZoom = gmapRef.value?.mapInstance?.getZoom();
	const filtersData = {
		...newFilters,
		bounds: currentBounds
			? getBoundsString(currentBounds)
			: filters.value.bounds,
		zoom: currentZoom || filters.value.zoom
	};
	mapController.updateClusters(filtersData as PropertyFilters);
}

async function applyFiltersFromFiltersBar(newFilters: PropertyFilters) {
	const locationChangeType = getLocationChangeType(newFilters, filters.value);

	const hasLocationChange = locationChangeType !== 'none';
	if (hasLocationChange) {
		clearSelectedClusters();
		await nextTick();
	}

	const baseFilters = createBaseFilters(newFilters, locationChangeType);
	const clusterFilters = { ...baseFilters };
	const propertyFilters = { ...baseFilters };

	updateClustersAndProperties(
		clusterFilters,
		propertyFilters,
		locationChangeType
	);

	return baseFilters;
}

async function handleSidebarApplyFilter(newFilters: PropertyFilters) {
	const changedKeys = getChangedKeysFilters(newFilters, filters.value);

	applyFiltersFromFiltersBar(newFilters);
	isSidebarActive.value = false;

	if (changedKeys.length === 0) return;
	await until(() => isFetching.value).toBeTruthy();
	await Promise.all([waitForFetchingToComplete(), waitForFilteredCount()]);

	const filterApplied = getFilterAppliedValues(
		newFilters,
		'moreFilters',
		changedKeys
	);
	filterApplied[MixpanelProps.TOTAL_RESULTS] = filteredCount.value;
	searchPropertyEvents.trackFilterApplied(filterApplied);
}

async function handleSidebarClearFilters() {
	clearFilters();
	await nextTick();

	const baseFilters = createBaseFilters(filters.value, 'removal');
	updateClustersForLocationChange(baseFilters, 'removal');

	clearFiltersAndClusters(baseFilters);

	isSidebarActive.value = false;
}

const infoLabel = useListingInfoLabel(() => {
	const { regions, cities, states, addressSlug } = filters.value;

	return {
		regions: regions || [],
		cities: cities || [],
		regionType: filters.value?.macroregions?.[0] || null,
		state: states?.[0] || '',
		addressSlug: addressSlug || ''
	};
});

const getPropertyTypeText = (filters: PropertyFilters, count: number) => {
	if (filters.propertyTypes?.length === 1) {
		const propertyType = filters.propertyTypes[0]!;

		return count > 1
			? PropertyTypePlurals[propertyType]
			: propertyTypesTranslations[propertyType];
	}

	return grammarInflection(count, 'imóvel', 'imóveis');
};
const propertyCountLabel = computed<string>(
	() =>
		`${filteredCount.value} ${getPropertyTypeText(filters.value, filteredCount.value).toLocaleLowerCase()}`
);

const mobilePropertyCountLabel = computed<string>(() => {
	const count = filteredCount.value;
	const article = grammarInflection(count, 'o', 'os');
	const propertyWord = grammarInflection(count, 'imóvel', 'imóveis');
	const countString = filteredCount.value > 1 ? count : '';

	return `Toque ou arraste para ver ${article} ${countString} ${propertyWord}`;
});

const shouldShowNextPageButton = computed<boolean>(() => {
	if (isFetchingForTheFirstTime.value) return false;

	return properties.value.length < filteredCount.value;
});

interface AddressSlugParts {
	visual: {
		locationName: string;
		street: string;
		streetNumber: string;
		neighborhood: string;
		city: string;
		state: string;
	};
	route: {
		locationName: string;
		street: string;
		streetNumber: string;
		neighborhood: string;
		city: string;
		state: string;
	};
}

const addressSlugDetails = computed<AddressSlugParts>(() => {
	if (!filters.value.addressSlug) {
		return {
			visual: {
				locationName: '',
				street: '',
				streetNumber: '',
				neighborhood: '',
				city: '',
				state: ''
			},
			route: {
				locationName: '',
				street: '',
				streetNumber: '',
				neighborhood: '',
				city: '',
				state: ''
			}
		};
	}

	// Split by comma and clean up
	const parts = filters.value.addressSlug.split(',').map(part => part.trim());

	let locationName = '';
	let street = '';
	let streetNumber = '';
	let neighborhood = '';
	let city = '';
	let state = '';

	// Check if it's a POI (has street with hyphen)
	if (parts[1]?.includes('-')) {
		// Format: "Parque Ibirapuera, Avenida Pedro Álvares Cabral - Vila Mariana, São Paulo - SP"
		locationName = parts[0] || '';
		const [streetPart = '', neighborhoodPart = ''] = (parts[1] || '')
			.split('-')
			.map(p => p.trim());
		street = streetPart;
		neighborhood = neighborhoodPart;

		// Handle city and state
		const [cityPart = '', statePart = ''] = (parts[2] || '')
			.split('-')
			.map(p => p.trim());
		city = cityPart;
		state = statePart;
	} else {
		// Check if it's a street with number
		const isStreetWithNumber = parts[1] && /^\d+$/.test(parts[1].trim());

		if (isStreetWithNumber) {
			// Format: "Rua Tabapuã, 25, Itaim Bibi, São Paulo - SP"
			street = parts[0] || '';
			streetNumber = parts[1] || '';
			neighborhood = parts[2] || '';
		} else {
			// Format: "Rua Tabapuã, Itaim Bibi, São Paulo - SP"
			street = parts[0] || '';
			neighborhood = parts[1] || '';
		}

		// In both cases, city and state are handled the same way
		const cityStatePart = parts[parts.length - 1] || '';
		const [cityPart = '', statePart = ''] = cityStatePart
			.split('-')
			.map(p => p.trim());
		city = cityPart;
		state = statePart;

		// For non-POI cases, street is also the location name
		locationName = street;
	}

	const visual = {
		locationName,
		street,
		streetNumber,
		neighborhood,
		city,
		state
	};

	const route = {
		locationName: slugify(visual.locationName),
		street: slugify(visual.street),
		streetNumber: slugify(visual.streetNumber),
		neighborhood: slugify(visual.neighborhood),
		city: slugify(visual.city),
		state: slugify(visual.state)
	};

	return {
		visual,
		route
	};
});

const isPointOfInterest = computed(
	() => filters.value.poiLocationLat && filters.value.poiLocationLng
);

const breadcrumbItems = computed<
	(BreadcrumbItem & { structuredDataLabel?: string })[]
>(() => {
	const state = dehyphenateString(filters.value.states?.[0] || 'SP');
	const city = dehyphenateString(filters.value.cities?.[0] || 'São Paulo');
	const region = dehyphenateString(filters.value.regions?.[0] || '');
	const addressSlug = filters.value.addressSlug?.split(',')[0] ?? '';

	const items: (BreadcrumbItem & { structuredDataLabel?: string })[] = [];

	// Adiciona o item inicial (Início)
	items.push({
		label: 'Início',
		structuredDataLabel: 'PilarHomes',
		route: '/',
		separator: false,
		type: 'home'
	});

	if (filters.value.macroregions?.length) {
		if (region) {
			// Case: macroregion + region + city
			// Shows: city -> region
			items.push({
				label: `${city} - ${state}`,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [state],
					cities: [city]
				}),
				separator: true,
				type: 'city'
			});
			items.push({
				label: region,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					macroregions: [filters.value.macroregions[0]!],
					states: [state],
					cities: [city],
					regions: [region]
				}),
				separator: true,
				type: 'region'
			});
		} else if (filters.value.cities?.length) {
			// Case: macroregion + city (no region)
			// Shows: city
			items.push({
				label: `${city} - ${state}`,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [state],
					cities: [city]
				}),
				separator: true,
				type: 'city'
			});
		} else {
			// Case: macroregion only (no city, no region)
			// Shows: macroregion
			items.push({
				label: `${filters.value.macroregions[0]!} - ${state}`,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					macroregions: [filters.value.macroregions[0]!],
					states: [state]
				}),
				separator: true,
				type: 'macroregion'
			});
		}

		return items;
	}
	if (state && city) {
		items.push({
			label: `${city} - ${state}`,
			route: PropertyFiltersPresenter.getRouteObjectFromFilters({
				states: [state],
				cities: [city]
			}),
			separator: true,
			type: 'city'
		});
	}
	if (isPointOfInterest.value) {
		const { route, visual } = addressSlugDetails.value;

		if (visual.neighborhood) {
			items.push({
				label: visual.neighborhood,
				separator: true,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [route.state],
					cities: [route.city],
					regions: [visual.neighborhood]
				}),
				type: 'region'
			});
		}
		if (visual.locationName) {
			items.push({
				label: visual.locationName,
				separator: true,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters(
					{
						states: [route.state],
						addressSlug: `${addressSlug}, ${visual.neighborhood}, ${visual.city} - ${visual.state}`,
						cities: [route.city],
						poiLocationLat: filters.value.poiLocationLat,
						poiLocationLng: filters.value.poiLocationLng
					},
					{
						page: 'imoveis'
					}
				),
				type: 'street'
			});
		}
		if (visual.streetNumber) {
			items.push({
				label: visual.streetNumber,
				separator: false,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [route.state],
					addressSlug: filters.value.addressSlug,
					locationSlug: filters.value.locationSlug,
					poiLocationLat: filters.value.poiLocationLat,
					poiLocationLng: filters.value.poiLocationLng
				}),
				type: 'street'
			});
		}

		return items || [];
	}
	if (region) {
		items.push({
			label: region,
			separator: true,
			route: PropertyFiltersPresenter.getRouteObjectFromFilters({
				states: [state],
				cities: [city],
				regions: [region]
			}),
			type: 'region'
		});
	}

	if (addressSlug) {
		items.push({
			label: addressSlug,
			separator: true,
			route: PropertyFiltersPresenter.getRouteObjectFromFilters({
				states: [state],
				cities: [city],
				regions: region ? [region] : undefined,
				addressSlug
			}),
			type: 'region'
		});
	}

	return items;
});

const websiteUrl = useRuntimeConfig().public.ORIGIN;

const breadcrumbListJsonLd = computed<WithContext<BreadcrumbList> | null>(() =>
	breadcrumbItems.value.length
		? ({
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: breadcrumbItems.value.map((item, index) => {
					const path: string =
						typeof item.route === 'string' ? item.route : item.route.path!;

					return {
						'@type': 'ListItem',
						position: index + 1,
						name: item.label,
						item: joinURL(websiteUrl, path)
					};
				})
			} satisfies WithContext<BreadcrumbList>)
		: null
);

useJsonld(() => breadcrumbListJsonLd.value);

const emptyStateData = computed<{ title: string; description: string } | null>(
	() => {
		if (isFetching.value) return null;

		if (hasProperties.value && hasLessThenTenProperties.value) {
			return {
				title: 'Esses foram os imóveis encontrados',
				description: `Ajuste os filtros para ver mais imóveis ${infoLabel.value}` //
			};
		}

		if (!isFetching.value && !hasProperties.value) {
			return {
				title: 'Nenhum imóvel encontrado',
				description: `Ajuste os filtros para ver imóveis ${infoLabel.value}`
			};
		}

		return null;
	}
);

whenever(
	() => emptyStateData.value,
	() => {
		trackEmptyState({
			filters: filters.value,
			currentPage: 'Listing Properties',
			propertiesCount: properties.value.length
		});
		// Zero-result SRP: PropertyListShortsSection only mounts when
		// properties.length > 0, so its handleShortsHasVideos page view never
		// fires here. Emit the 'listed properties' page view from the page for
		// empty results — mutually exclusive with the shorts-section path.
		trackPageView({
			current_page: 'listed properties',
			previous_page_url: document.referrer
		});
	}
);

const bodyElement = ref<HTMLElement | null>(null);
const isBodyScrollLocked = useScrollLock(bodyElement);

watch(
	isSidebarActive,
	shouldDisableBodyScroll =>
		(isBodyScrollLocked.value = shouldDisableBodyScroll ? true : false)
);

function handleOpenMapVisionButtonClick(clickLocation: 'cta' | 'map') {
	trackMapListingClicked({
		current_page: 'listed properties',
		click_location: clickLocation,
		cta_text: 'Ver imóveis no Mapa',
		destination_url: PropertyFiltersPresenter.getPathFromFilters(
			filters.value,
			{},
			{ page: 'mapa-de-imoveis' }
		),
		property_count: filteredCount.value,
		filter_city: filters.value.cities?.[0],
		filter_region: filters.value.macroregions?.[0],
		filter_neighborhood: filters.value.regions?.[0]
	});
}

function handleBreadcrumbClicked(breadcrumbItem: BreadcrumbItemWithIndex) {
	const appliedFilters = PropertyFiltersPresenter.getFiltersFromPath(
		(breadcrumbItem.route as Record<string, string>).path!,
		filters.value
	);

	if (appliedFilters.addressSlug) {
		let city = '';
		let state = '';

		// Verifica se o addressSlug tem vírgulas (formato antigo)
		if (appliedFilters.addressSlug.includes(',')) {
			const [, parsedCity, parsedState] = appliedFilters.addressSlug.split(',');
			city = parsedCity?.trim() || '';
			state = parsedState?.trim() || '';
		} else {
			// Para URLs como "batel-curitiba-pr", extrai cidade e estado do slug
			const slugParts = appliedFilters.addressSlug.split('-');
			if (slugParts.length >= 3) {
				// Assume que os últimos dois elementos são cidade-estado
				city = slugParts[slugParts.length - 2] || '';
				state = slugParts[slugParts.length - 1] || '';
			}
		}

		if (city && state) {
			const slugifiedAddress = slugify(`${city}, ${state}`);
			const locationSlug = `${slugifiedAddress}-brasil`;
			appliedFilters.locationSlug = locationSlug;
		}
	}

	if (breadcrumbItem.type === 'macroregion') {
		const macroregion = (breadcrumbItem.route as any).query.macroregions;

		appliedFilters.macroregions = [macroregion];
	}

	if (breadcrumbItem.route !== '/') {
		applyFiltersFromFiltersBar(appliedFilters);
	}

	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.CLICK_HOME]: breadcrumbItem.type === 'home',
		[MixpanelProps.CLICK_CITY]: breadcrumbItem.type === 'city',
		[MixpanelProps.CLICK_NEIGHBORHOOD]: breadcrumbItem.type === 'region',
		[MixpanelProps.CLICK_MACROREGION]: breadcrumbItem.type === 'macroregion',
		[MixpanelProps.CLICK_STREET]: breadcrumbItem.type === 'street',
		[MixpanelProps.CLICK_STATE]: breadcrumbItem.type === 'state',
		[MixpanelProps.CTA_TEXT]: breadcrumbItem.label,
		[MixpanelProps.DESTINATION_URL]:
			(breadcrumbItem.route as Record<string, string>).path || '/'
	});
}
const clickCount = ref(0);

function handleViewMoreClicked(page: string) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: page,
		[MixpanelProps.DESTINATION_URL]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: 'Ver mais',
		[MixpanelProps.CLICK_COUNT]: clickCount.value++
	});
}

const eventCleanupFunctions: (() => void)[] = [];

// Register a one-shot google.maps `idle` listener that auto-removes
// after firing AND on component unmount. Without the unmount path the
// listener would linger on the map instance if the user navigates away
// before `idle` fires, and the callback would close over stale refs.
function listenIdleOnce(
	mapInstance: google.maps.Map,
	callback: () => void
): void {
	let cleanup: (() => void) | null = null;
	const listener = mapInstance.addListener('idle', () => {
		cleanup?.();
		callback();
	});
	cleanup = () => {
		if (!cleanup) return;
		cleanup = null;
		google.maps.event.removeListener(listener);
		const idx = eventCleanupFunctions.indexOf(removeFromRegistry);
		if (idx !== -1) eventCleanupFunctions.splice(idx, 1);
	};
	const removeFromRegistry = () => cleanup?.();
	eventCleanupFunctions.push(removeFromRegistry);
}

const propertyCardHoverBus = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardHover
);
const propertyCardUnhoverBus = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardUnhover
);
const viewMoreBus = useEventBus(EventBusKeys.viewMoreClicked);

eventCleanupFunctions.push(
	onClientEventBus(propertyCardHoverBus, handleCardHover),
	onClientEventBus(propertyCardUnhoverBus, handleCardUnhover),
	onClientEventBus(viewMoreBus, handleViewMoreClicked)
);

function handleReorderClicked(value: OrdenationDropdownOption['value']) {
	moveBackToInitialPosition();
	trackReorderClicked({
		[MixpanelProps.CLICK_EXCLUSIVES]: value === 'pilar_exclusivities',
		[MixpanelProps.CLICK_HIGHLIGHTS]: value === 'highlights',
		[MixpanelProps.CLICK_LOWEST_PRICE]: value === 'asking_price_asc',
		[MixpanelProps.CLICK_HIGHEST_PRICE]: value === 'asking_price_desc',
		[MixpanelProps.CLICK_FEWER_PHOTOS]: value === 'images_count_asc',
		[MixpanelProps.CLICK_MORE_PHOTOS]: value === 'images_count_desc',
		[MixpanelProps.CLICK_MORE_RELEVANT]: value === 'default_ordenation',
		[MixpanelProps.CLICK_MORE_RECENT]: value === 'wishlist_recent',
		[MixpanelProps.CLICK_OLDEST]: value === 'wishlist_oldest'
	});
}

function handleRegionClicked({
	ctaText,
	destinationUrl
}: RegionInfoLinkClickedEmitProps) {
	trackLinkClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.SECTION_NAME]: 'neighborhood information',
		[MixpanelProps.LINK_TEXT]: ctaText,
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.LINK_CATEGORY]: 'Informações do bairro'
	});
}

/**
 * O número de imóveis no JSON-LD é limitado a 10 pois não vamos exibir mais do que isso na primeira página,
 * que é o que vem renderizado no servidor. Além disso, se atualizarmos o JSON-LD toda vez que mais imóveis
 * forem carregados no cliente, pode ser que impacte a performance da página.
 **/
usePropertiesListingJsonLd(
	() => (properties.value || []).slice(0, DEFAULT_SERVER_PAGINATION.page),
	{
		pageTitle: () => pageTitle.value,
		pageDescription: () => pageDescription.value
	}
);

const addressGmapMarker = computed(() => {
	if (!filters.value.addressSlug) return '';

	const [firstPart, secondPart] = filters.value.addressSlug.split(',');

	if (!secondPart?.trim()) return firstPart;

	const hasNumberAfterComma = /^\s*\d/.test(secondPart);

	return hasNumberAfterComma
		? `${firstPart},${secondPart.split(',')[0]}`
		: firstPart;
});

const markerPosition = computed<{ lat: number; lng: number } | null>(() => {
	if (!filters.value.poiLocationLat || !filters.value.poiLocationLng) {
		return null;
	}

	return {
		lat: filters.value.poiLocationLat,
		lng: filters.value.poiLocationLng
	};
});

async function handleOpenMapFromMobileButton() {
	await propertyListSectionRef.value?.scrollToTop();
	handleOpenMapVisionButtonClick('cta');
	// Dar um frame pro mapa renderizar antes de fazer o snap visual
	await new Promise(resolve => requestAnimationFrame(resolve));
	toggleToClosedWhenOpen();
}

const snapHeights = computed<SheetSnapHeights>(() => {
	const isEmptyStateWithNoProperties =
		emptyStateData.value && properties.value.length === 0;

	return {
		1: isEmptyStateWithNoProperties ? 400 : 250,
		// Peek = 64: horizontal CTA bar height = 64px (h-16) to perfectly
		// match the appearance and tap target of the legacy fixed bottom CTA.
		2: 64
	};
});

// Scaffold de bottom-sheet: encapsula mecanica do drawer e expoe
// estado/acoes via ref + slots.
const bottomSheetScaffoldRef = ref<InstanceType<
	typeof BottomSheetScaffold
> | null>(null);

const menuBottomLevel = computed<SheetLevel>(
	() => bottomSheetScaffoldRef.value?.menuBottomLevel ?? SheetLevel.CLOSED
);

function moveBackToInitialPosition(): void {
	bottomSheetScaffoldRef.value?.moveBackToInitialPosition();
}

function toggleToClosedWhenOpen(): void {
	bottomSheetScaffoldRef.value?.toggleToClosedWhenOpen();
}

function toggleToHalfOpenWhenClosed(): void {
	bottomSheetScaffoldRef.value?.toggleToHalfOpenWhenClosed();
}

function selectClustersFromH3Ids(): void {
	if (!filters.value.h3Ids?.length || !clusters.value.length) {
		return;
	}

	const h3IdsSet = new Set(filters.value.h3Ids);

	clusters.value.forEach(cluster => {
		const clusterId = cluster.geometry.id;

		if (h3IdsSet.has(clusterId) && !selectedClustersData.value.has(clusterId)) {
			originalHandleClusterSelectionChanged({
				clusterId: cluster.geometry.id,
				isSelected: true
			});
		}
	});
}

const isMounted = ref(false);
const isMobileScreen = useBreakpoints(screenSizesInPixels).smaller('lg');
const isHydratedEntry = useNuxtApp().isHydrating;

const isMapVisuallyHidden = computed(() => {
	if (!isMounted.value || !isMobileScreen.value) return false;
	// Usa a posição real do sheet (cobrindo a viewport), não o snap mais
	// próximo: assim o mapa reaparece no primeiro pixel da descida, sem
	// fundo branco enquanto o drawer desce.
	const isSheetFullscreen =
		bottomSheetScaffoldRef.value?.isSheetFullscreen ?? false;
	const isDragging = bottomSheetScaffoldRef.value?.isDragging ?? false;
	const isTransitioning =
		bottomSheetScaffoldRef.value?.isTransitioning ?? false;
	return isSheetFullscreen && !isDragging && !isTransitioning;
});

// Gate do mapa: no mobile o mapa nasce coberto pelo bottom sheet. Controla o
// v-if do GmapMap porque o componente chama initGoogleMaps() no proprio mount.
const isMapEnabled = ref(false);

function enableMap(): void {
	if (isMapEnabled.value) return;
	isMapEnabled.value = true;
}

// O sheet ancora sobre o mapa frames depois do mount; avaliar visibilidade
// so depois do repouso.
const MAP_GATE_SETTLE_MS = 700;
let mapGateSettleTimer: ReturnType<typeof setTimeout> | undefined;
let stopMapUncoverWatch: (() => void) | undefined;
let stopDesktopBreakpointWatch: (() => void) | undefined;

function armMobileMapGate(): void {
	mapGateSettleTimer = setTimeout(() => {
		// Bounds + zoom na entrada sao evidencia de um deep link criado pelo mapa.
		// No Safari iOS, a restauracao do sheet pode mante-lo classificado como
		// coberto ate o primeiro toque, mesmo quando essa visao precisa do mapa.
		const isMapDeepLink = Boolean(
			isHydratedEntry && filters.value.bounds && filters.value.zoom
		);

		if (isMapDeepLink || !isMapVisuallyHidden.value) {
			enableMap();
			return;
		}
		// Coberto: `isMapVisuallyHidden` ja vira false no primeiro pixel de drag,
		// entao qualquer gesto no sheet destrava sem listener proprio.
		stopMapUncoverWatch = watch(isMapVisuallyHidden, isHidden => {
			if (isHidden) return;
			enableMap();
			stopMapUncoverWatch?.();
		});
	}, MAP_GATE_SETTLE_MS);
}

onMounted(() => {
	isMounted.value = true;
	bodyElement.value = document.body;
	// Desktop: mapa ocupa metade da viewport, continua carregando no mount.
	if (isMobileScreen.value) {
		armMobileMapGate();
		// Resize para desktop torna o mapa visivel sem gesto no sheet.
		stopDesktopBreakpointWatch = watch(isMobileScreen, isMobile => {
			if (isMobile) return;
			enableMap();
			stopDesktopBreakpointWatch?.();
		});
	} else {
		enableMap();
	}
	captureLastSrpUrlFromRoute(route);
	setupLastSrpUrlActivityListeners();
});

onBeforeUnmount(() => {
	if (mapGateSettleTimer) clearTimeout(mapGateSettleTimer);
	stopMapUncoverWatch?.();
	stopDesktopBreakpointWatch?.();
});

watch(
	() => route.fullPath,
	() => {
		captureLastSrpUrlFromRoute(route);
		setupLastSrpUrlActivityListeners();
	}
);

const {
	clusters,
	selectedClustersData,
	selectedH3Ids,
	markersKey,
	updateClusters: originalUpdateClusters,
	handleClusterSelectionChanged: originalHandleClusterSelectionChanged,
	handleClusterDeselectedByResolution:
		originalHandleClusterDeselectedByResolution,
	normalizeClusterCoordinates,
	clearSelectedClusters
} = useMapClusters({
	gmapRef
});

// Reactive wrapper that only returns clusters when map instance is available
const readyClusters = computed(() => {
	const mapInstance = gmapRef.value?.mapInstance;
	const currentClusters = clusters.value;

	if (!mapInstance) {
		return [];
	}

	return currentClusters;
});

const mapController = useMapController({
	filters,
	clusters,
	gmapRef,
	selectedClustersData,
	normalizeClusterCoordinates,
	originalUpdateClusters,
	pageConfig: {
		page: 'imoveis'
	}
});

const DRAWING_ROUTE_IGNORED_FILTER_KEYS = new Set(['h3Ids', 'bounds', 'zoom']);

function getRoutePropertyFilters(fullPath: string): PropertyFilters {
	return PropertyFiltersPresenter.getFiltersFromPath(
		fullPath,
		{ ...DEFAULT_FILTER },
		'imoveis'
	);
}

function normalizeDrawingRouteFilterValue(value: unknown): unknown {
	if (!Array.isArray(value)) return value;

	return [...value].sort((first, second) =>
		String(first).localeCompare(String(second))
	);
}

function getDrawingRouteSnapshot(fullPath: string): {
	filtersKey: string;
	h3IdsKey: string;
} {
	const routeFilters = getRoutePropertyFilters(fullPath);
	const normalizedFilterEntries = Object.entries(routeFilters)
		.filter(
			([key, value]) =>
				!DRAWING_ROUTE_IGNORED_FILTER_KEYS.has(key) && value !== undefined
		)
		.map(([key, value]) => [key, normalizeDrawingRouteFilterValue(value)])
		.sort(([firstKey], [secondKey]) =>
			String(firstKey).localeCompare(String(secondKey))
		);

	return {
		filtersKey: JSON.stringify(normalizedFilterEntries),
		h3IdsKey: getNormalizedH3IdsKey(routeFilters.h3Ids)
	};
}

let pendingDrawingH3IdsKey: string | null = null;
let hasDrawingSelection = false;
const h3SelectionUrlSyncQueue = createH3SelectionUrlSyncQueue({
	getRouteH3Ids: () => getRoutePropertyFilters(route.fullPath).h3Ids,
	navigate: navigateToH3Ids
});

onBeforeRouteLeave(() => h3SelectionUrlSyncQueue.dispose());
onBeforeUnmount(() => h3SelectionUrlSyncQueue.dispose());

function handleDrawingClustersSelected(clusterIds: string[]): void {
	hasDrawingSelection = clusterIds.length > 0;
	clearSelectedClusters();

	clusterIds.forEach(clusterId => {
		originalHandleClusterSelectionChanged({
			clusterId,
			isSelected: true
		});
	});

	if (clusterIds.length > 0) {
		const nextH3IdsKey = getNormalizedH3IdsKey(clusterIds);
		pendingDrawingH3IdsKey =
			getDrawingRouteSnapshot(route.fullPath).h3IdsKey === nextH3IdsKey
				? null
				: nextH3IdsKey;
		updateUrlForH3Ids(clusterIds);
		toggleToHalfOpenWhenClosed();

		trackMapListingInteracted({
			current_page: 'Listing Map',
			click_squares: true,
			zoom_click: false,
			final_click: true,
			square_count: clusterIds.length,
			property_count: clusterIds.reduce((total, clusterId) => {
				const cluster = clusters.value.find(c => c.geometry.id === clusterId);
				return total + (cluster?.properties.pointCount || 0);
			}, 0)
		});
	}
}

const drawingHighlightedClusterIds = ref<Set<string>>(new Set());

const {
	isDrawingMode,
	isDrawing,
	currentDrawingMode,
	toggleDrawingMode,
	resetDrawing
} = useMapDrawing({
	gmapRef,
	clusters,
	onClustersSelected: handleDrawingClustersSelected,
	onHoveredClustersChanged: clusterIds => {
		drawingHighlightedClusterIds.value = new Set(clusterIds);
	}
});

watch(
	() => route.fullPath,
	(newFullPath, oldFullPath) => {
		const newSnapshot = getDrawingRouteSnapshot(newFullPath);
		const oldSnapshot = getDrawingRouteSnapshot(oldFullPath);
		const filtersChanged = newSnapshot.filtersKey !== oldSnapshot.filtersKey;
		const h3IdsChanged = newSnapshot.h3IdsKey !== oldSnapshot.h3IdsKey;
		const isDrawingSelectionNavigation =
			!filtersChanged &&
			h3IdsChanged &&
			pendingDrawingH3IdsKey === newSnapshot.h3IdsKey;

		// Consume the origin marker on the first atomic route transition. This also
		// prevents a stale drawing selection from suppressing a later back/forward.
		pendingDrawingH3IdsKey = null;

		if ((filtersChanged || h3IdsChanged) && !isDrawingSelectionNavigation) {
			hasDrawingSelection = false;
			resetDrawing();
		}
	},
	{ flush: 'sync' }
);

function getClusterKey(clusterId: string): string {
	const isSelected = selectedClustersData.value.has(clusterId);

	if (isSelected) {
		return `selected-${clusterId}`;
	} else {
		return `unselected-${markersKey.value}-${clusterId}`;
	}
}

function updateUrlForH3Ids(
	newH3Ids: string[],
	options: { force?: boolean } = {}
): void {
	h3SelectionUrlSyncQueue.request(newH3Ids, options);
}

function clearSelectionAndPendingNavigation(): void {
	pendingDrawingH3IdsKey = null;
	hasDrawingSelection = false;
	clearSelectedClusters();
	updateUrlForH3Ids([], { force: true });
}

const drawingModeCoordinator = createMapDrawingModeCoordinator({
	hasSelection: () =>
		hasDrawingSelection || selectedClustersData.value.size > 0,
	resetDrawing,
	clearSelection: clearSelectionAndPendingNavigation,
	whenSelectionSyncIdle: () => h3SelectionUrlSyncQueue.whenIdle(),
	toggleDrawingMode
});

function handleDrawingModeToggle(mode: DrawingMode): void {
	void drawingModeCoordinator.toggle(mode);
}

onBeforeUnmount(() => drawingModeCoordinator.dispose());

function navigateToH3Ids(newH3Ids: string[]): Promise<unknown> {
	const routeFilters = getRoutePropertyFilters(route.fullPath);
	const newFilters = { ...routeFilters, h3Ids: newH3Ids };
	const mapInstance = gmapRef.value?.mapInstance;
	const currentZoom = mapInstance?.getZoom();

	if (!newH3Ids?.length) {
		const currentBounds = mapInstance?.getBounds();

		if (currentBounds) {
			newFilters.bounds = getBoundsString(currentBounds);
		}
	}

	newFilters.zoom = currentZoom;

	const regionsFilters = {
		regions: newFilters.regions,
		cities: newFilters.cities,
		states: newFilters.states,
		macroregions: newFilters.macroregions
	};

	return router.push(
		PropertyFiltersPresenter.getPathFromFilters(newFilters, regionsFilters, {
			page: 'imoveis'
		})
	);
}

watch(selectedH3Ids, (newH3Ids, oldH3Ids) => {
	if (mapController.state.currentState.value !== MapState.READY) return;

	updateUrlForH3Ids(newH3Ids ?? []);

	const hasNewSelection = (newH3Ids?.length ?? 0) > (oldH3Ids?.length || 0);

	if (hasNewSelection) {
		toggleToHalfOpenWhenClosed();
	}
});

const mapDragEndGuard = createMapDragEndGuard({
	onDragEnd: () => mapController.handleDragEnd(),
	onConfirmedDragEnd: () => resetDrawing()
});

function handleDragStart(): void {
	mapDragEndGuard.markDragStarted();
}

function handleDragEnd(): void {
	mapDragEndGuard.handleDragEnd();
}

function handleZoomChanged(zoom: number | undefined): void {
	resetDrawing();

	if (hasDrawingSelection) {
		clearSelectionAndPendingNavigation();
	}

	if (!gmapRef.value?.mapInstance || !zoom) return;
	mapController.handleZoomChanged(zoom);
}

function getBoundsString(bounds: google.maps.LatLngBounds): string {
	const { north, south, east, west } = bounds.toJSON();
	return `${west},${south},${east},${north}`;
}

function updateUrlAndListing(filtersData: PropertyFilters) {
	const regionsFilters = {
		regions: filtersData.regions,
		cities: filtersData.cities,
		states: filtersData.states,
		macroregions: filtersData.macroregions
	};
	router.push(
		PropertyFiltersPresenter.getPathFromFilters(filtersData, regionsFilters, {
			page: 'imoveis'
		})
	);
}

function clearFiltersAndClusters(
	newFilters: PropertyFilters,
	regionsFilters?: {
		regions?: string[];
		cities?: string[];
		states?: string[];
		macroregions?: string[];
	}
) {
	const hadSelectedClusters = selectedClustersData.value.size > 0;
	if (hadSelectedClusters) {
		clearSelectedClusters();
	}

	const finalFilters = { ...newFilters, h3Ids: [] };

	router.push(
		PropertyFiltersPresenter.getPathFromFilters(
			finalFilters,
			regionsFilters || {},
			{
				page: 'imoveis'
			}
		)
	);
}

const resetDrawingForClusterChange = createMapDrawingResetGuard({
	hasDrawingSelection: () => hasDrawingSelection,
	resetDrawing
});

watch(clusters, resetDrawingForClusterChange);

watch(
	() => markersKey.value,
	() => {
		const selectedClusterIds = new Set(selectedClustersData.value.keys());

		const clustersToDelete = Array.from(clusterMarkersRefs.value.keys()).filter(
			clusterId => !selectedClusterIds.has(clusterId)
		);

		clustersToDelete.forEach(clusterId => {
			clusterMarkersRefs.value.delete(clusterId);
		});
	}
);

watchOnce(clusters as any, (newClusters: any, oldClusters: any) => {
	const shouldAutoSelect =
		filters.value.h3Ids?.length &&
		newClusters.length > 0 &&
		(!oldClusters || oldClusters.length === 0);

	if (shouldAutoSelect) {
		const mapInstance = gmapRef.value?.mapInstance;

		if (mapInstance) {
			selectClustersFromH3Ids();
			listenIdleOnce(mapInstance, () => mapController.clearProgrammaticFlag());
		}
	}
});

onUnmounted(() => {
	eventCleanupFunctions.forEach(cleanup => cleanup());
	eventCleanupFunctions.length = 0;
	clusterMarkersRefs.value.clear();
	if (import.meta.client) {
		const root = document.documentElement.style;
		root.removeProperty('--sheet-top-chrome');
		root.removeProperty('--chrome-height');
	}
});

const shouldShowSeeAllCityButton = computed(() => {
	return !!(
		filters.value.regions?.length ||
		filters.value.addressSlug ||
		(filters.value.poiLocationLat && filters.value.poiLocationLng)
	);
});

function handleSeeAllCity() {
	trackBoundariesCleared({
		current_page: 'Listing Map',
		property_count: totalSelectedPropertiesCount.value,
		neighborhoods_name: filters.value.regions?.join(', ') || '',
		square_count: selectedClustersData.value.size
	});

	const newFilters = { ...filters.value };

	let cities = newFilters.cities || [];
	let states = newFilters.states || [];

	if (newFilters.addressSlug && (!cities.length || !states.length)) {
		const { city, state } = addressSlugDetails.value.visual;
		if (city && !cities.length) {
			cities = [city];
		}
		if (state && !states.length) {
			states = [state];
		}
	}

	newFilters.cities = cities;
	newFilters.states = states;

	newFilters.regions = [];
	newFilters.addressSlug = '';
	newFilters.poiLocationLat = undefined;
	newFilters.poiLocationLng = undefined;
	newFilters.locationSlug = undefined;

	const currentBounds = gmapRef.value?.mapInstance?.getBounds();
	const currentZoom = gmapRef.value?.mapInstance?.getZoom();

	if (currentBounds && currentZoom) {
		newFilters.bounds = getBoundsString(currentBounds);
		newFilters.zoom = currentZoom;
	}

	mapController.processSeeAllCity('filters-bar');

	const clusterFilters = {
		...newFilters,
		h3Ids: [],
		bounds: newFilters.bounds,
		zoom: newFilters.zoom
	};

	const regionsFilters = {
		regions: newFilters.regions,
		cities: newFilters.cities,
		states: newFilters.states,
		macroregions: newFilters.macroregions
	};

	clearFiltersAndClusters(newFilters, regionsFilters);
	mapController.forceUpdateClusters(clusterFilters as PropertyFilters);
}

function handleClearSelectedClusters() {
	trackSelectionCleared({
		current_page: 'Listing Map',
		property_count: totalSelectedPropertiesCount.value,
		square_count: selectedClustersData.value.size || 1
	});

	clearSelectionAndPendingNavigation();
	resetDrawing();
}

async function handleClusterClicked(clusterData: BaseClusterData) {
	const cluster = clusters.value.find(
		c => c.geometry.id === clusterData.clusterId
	);
	if (!cluster) return;

	mapController.handleClusterNavigation();
	await propertyListSectionRef.value?.scrollToTop();
	toggleToHalfOpenWhenClosed();
	trackMapListingInteracted({
		current_page: 'Listing Map',
		click_squares: true,
		zoom_click: cluster.properties.pointCount > 20,
		final_click: cluster.properties.pointCount <= 20,
		square_count: selectedClustersData.value.size || 1,
		property_count: totalSelectedPropertiesCount.value
	});

	if (!gmapRef.value?.mapInstance) return;

	listenIdleOnce(gmapRef.value.mapInstance, () => {
		nextTick(() => {
			const bounds = gmapRef.value?.mapInstance?.getBounds();
			if (bounds) {
				mapController.updateMapBounds(bounds);
			}
		});
	});
}

function findClusterMarkerByH3Ids(
	h3ClusterIds: H3ClusterId[]
): InstanceType<typeof GmapClusterMarkerComponent> | undefined {
	if (selectedClustersData.value.size === 1) return undefined;
	const currentZoom = gmapRef.value?.mapInstance?.getZoom();
	const clusterId = h3ClusterIds.find(x => x.zoom === currentZoom)?.id;
	const cluster = clusters.value.find(c => c.geometry.id === clusterId);
	if (!cluster) return undefined;
	return clusterMarkersRefs.value.get(cluster.geometry.id);
}

function handleCardHover(h3ClusterIds: H3ClusterId[]) {
	findClusterMarkerByH3Ids(h3ClusterIds)?.triggerMarkerHover();
}

function handleCardUnhover(h3ClusterIds: H3ClusterId[]) {
	findClusterMarkerByH3Ids(h3ClusterIds)?.triggerMarkerUnhover();
}

function setClusterMarkerRef(
	clusterId: string,
	markerRef: Element | ComponentPublicInstance | null
) {
	if (markerRef && '$el' in markerRef) {
		clusterMarkersRefs.value.set(
			clusterId,
			markerRef as InstanceType<typeof GmapClusterMarkerComponent>
		);
	} else {
		clusterMarkersRefs.value.delete(clusterId);
	}
}

async function handleFilterLinkClicked(newFilters: PropertyFilters) {
	const hadSelectedClusters = selectedClustersData.value.size > 0;
	if (hadSelectedClusters) {
		clearSelectedClusters();
		await nextTick();
	}
	const baseFilters = { ...newFilters, bounds: undefined, zoom: undefined };
	const clusterFilters = { ...baseFilters, h3Ids: [] };
	const propertyFilters = { ...baseFilters, h3Ids: [] };

	updateClustersForLocationChange(clusterFilters, 'substitution');

	const regionsFilters = {
		regions: propertyFilters.regions,
		cities: propertyFilters.cities,
		states: propertyFilters.states,
		macroregions: propertyFilters.macroregions
	};
	router.push(
		cleanLocationQueryFromPath(
			PropertyFiltersPresenter.getPathFromFilters(
				propertyFilters,
				regionsFilters,
				{ page: 'imoveis' }
			)
		)
	);

	return baseFilters;
}

const isViewMoreButtonInViewport = ref(false);
const viewMoreButtonPosition = ref<{ top: number } | undefined>();
const isPropertyListFooterVisible = ref(false);

const hasEmptyState = computed(() => !!emptyStateData.value);

const viewMoreButtonPositionComputation = computed(() => {
	if (hasEmptyState.value) return undefined;

	return viewMoreButtonPosition.value;
});

function handleViewMoreButtonVisibilityChanged(isVisible: boolean) {
	isViewMoreButtonInViewport.value = isVisible;
}

function handleViewMoreButtonPositionChanged(position: { top: number }) {
	viewMoreButtonPosition.value = position;
}

function handleFooterVisibilityChanged(isVisible: boolean) {
	isPropertyListFooterVisible.value = isVisible;
}

const totalSelectedPropertiesCount = computed(() => {
	let total = 0;
	for (const clusterId of selectedClustersData.value.keys()) {
		const cluster = clusters.value.find(c => c.geometry.id === clusterId);
		if (cluster) {
			total += cluster.properties.pointCount;
		}
	}
	return total;
});

function trackClusterFinalClick() {
	nextTick(() => {
		trackMapListingInteracted({
			current_page: 'Listing Map',
			click_squares: true,
			zoom_click: false,
			final_click: true,
			square_count: selectedClustersData.value.size || 1,
			property_count: totalSelectedPropertiesCount.value
		});
	});
}

function handleClusterSelectionChanged(params: {
	clusterId: string;
	isSelected: boolean;
}) {
	const cluster = clusters.value.find(c => c.geometry.id === params.clusterId);
	if (!cluster) return;
	hasDrawingSelection = false;
	resetDrawing();
	originalHandleClusterSelectionChanged(params);
	trackClusterFinalClick();
}

function handleClusterDeselectedByResolution(data: { clusterId: string }) {
	resetDrawing();

	if (hasDrawingSelection) {
		clearSelectionAndPendingNavigation();
	} else {
		originalHandleClusterDeselectedByResolution(data);
	}

	trackClusterFinalClick();
}

const emptyStateRef = computed(() => {
	return propertyListSectionRef.value?.emptyStateRef;
});

const mobileScrollButtonHideAfterElement = computed(() => {
	if (emptyStateData.value) {
		return (emptyStateRef.value?.$el as HTMLElement) || null;
	}
	// Esconde o botão a partir da seção "sobre nós" (NavigationSectionNavigation) se estiver disponível,
	// caindo de volta para o footer caso contrário.
	const aboutSection = propertyListSectionRef.value?.aboutSectionRef;
	if (aboutSection?.$el) {
		return aboutSection.$el as HTMLElement;
	}
	return null;
});

const { headerDragOffsetY, isHeaderHidden, resetHeaderToVisible } =
	usePropertiesListingHeader();

const isMobileMapVisible = computed(
	() => isMounted.value && isMobileScreen.value && !isMapVisuallyHidden.value
);

function hideHeaderForMobileMap(): void {
	headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MAX_OFFSET;
}

// SRP->SRP reusa a MESMA instancia da pagina (key estatica em definePageMeta),
// entao mount/unmount nao re-disparam e o reset do layout (watch isMapView) nao
// re-roda. Sem isto, a navbar deixada oculta numa listagem vazaria oculta para
// a proxima. Mudancas de bounds/zoom tambem alteram fullPath; quando o mapa
// mobile esta visivel, elas devem preservar a navbar oculta.
watch(
	() => route.fullPath,
	() => {
		if (isMobileMapVisible.value) {
			hideHeaderForMobileMap();
			return;
		}

		resetHeaderToVisible();
	}
);

// Garante a mesma regra ao abrir o mapa sem que haja mudanca de rota. Ao
// voltar para a lista, o proprio bottom sheet continua responsavel por animar
// o header; no desktop removemos qualquer offset mobile remanescente.
watch(isMobileMapVisible, isVisible => {
	if (isVisible) {
		hideHeaderForMobileMap();
	} else if (!isMobileScreen.value) {
		resetHeaderToVisible();
	}
});

const DRAWING_INDICATOR_GAP_PX = 16;
const drawingIndicatorStyle = computed(() => {
	if (!isMobileScreen.value) return undefined;

	const sheetVisibleHeight =
		snapHeights.value[SheetLevel.FULLY_OPEN] +
		Math.max(0, bottomSheetScaffoldRef.value?.currentY ?? 0);

	return {
		bottom: `calc(${sheetVisibleHeight + DRAWING_INDICATOR_GAP_PX}px + env(safe-area-inset-bottom, 0px))`
	};
});

// Durante o scroll do documento (mobile), avisamos os CardCarousel para
// renderizarem só a capa, reduzindo o custo de paint no fling rápido do iOS.
// `idle: 150` = ~150ms após o último evento de scroll já volta a montar o Embla.
// Detector próprio (sem ler geometria) em vez do `useScroll` do VueUse, que
// computa `arrivedState` lendo offsetHeight/scrollHeight a cada evento de scroll
// (reflow forçado / layout thrashing). Aqui só precisamos do booleano.
const { isScrolling: isWindowScrolling } = useWindowScrolling(150);

const isCarouselScrolling = computed(
	() => isMobileScreen.value && isWindowScrolling.value
);

provideCarouselScrolling({
	isScrolling: isCarouselScrolling,
	lazyInitialMount: true
});

const chromeHeightPx = computed(() => {
	const navbarHeight = Math.max(
		0,
		HEADER_OFFSET_CONFIG.MOBILE_HEIGHT - headerDragOffsetY.value
	);
	return `${navbarHeight + FILTER_BAR_HEIGHT}px`;
});

const pageContentStyle = computed(() => {
	if (!isMounted.value || !isMobileScreen.value) return undefined;
	return {
		paddingTop: `${MOBILE_CHROME_HEIGHT}px`,
		'--chrome-height': chromeHeightPx.value
	};
});

// Mantem o sticky-top do drawer alinhado ao header dinamico no mobile.
watch(
	[chromeHeightPx, isMobileScreen],
	([value, isMobile]) => {
		if (import.meta.server) return;
		const root = document.documentElement.style;
		if (isMobile) {
			root.setProperty('--sheet-top-chrome', value);
			root.setProperty('--chrome-height', value);
		} else {
			root.removeProperty('--sheet-top-chrome');
			root.removeProperty('--chrome-height');
		}
	},
	{ immediate: true }
);

const sectionHeightClass = computed(() => {
	if (isHeaderHidden.value) {
		return 'h-[calc(100dvh-81px)] lg:h-[calc(100dvh-163px)]';
	}

	return 'h-[calc(100dvh-141px)] lg:h-[calc(100dvh-163px)]';
});
</script>

<template>
	<ClientOnly>
		<template #fallback>
			<LazyFiltersFiltersBar
				fixed
				:filters="filters"
				page="imoveis"
				:is-fetching-properties="isFetching"
				:count-properties="filteredCount || 0"
				class="h-[65px] lg:h-20.75"
			/>
		</template>

		<Teleport to="#filters-bar">
			<LazyFiltersFiltersBar
				v-model:is-sidebar-active="isSidebarActive"
				fixed
				:filters="filters"
				:applied-filter-keys="appliedFilterKeys"
				:number-of-applied-closed-filters="numberOfAppliedClosedFilters"
				:count-properties="filteredCount"
				:is-fetching-properties="isFetching"
				page="imoveis"
				class="h-[65px] lg:h-20.75"
				@update:filters="applyFiltersFromFiltersBar($event)"
				@filter-applied-for-code-search="trackFilterApplied"
				@filter-applied-for-condo="trackFilterApplied"
			/>
		</Teleport>
	</ClientOnly>

	<ClientOnly>
		<Teleport to="#filters-sidebar">
			<LazyFiltersFiltersSidebar
				v-model:is-active="isSidebarActive"
				:filters="filters"
				:selected-agent="selectedAgent"
				:selected-agent-resolution="selectedAgentResolution"
				:preserve-drafts-on-sync="preserveSidebarDrafts"
				page-name="imoveis"
				:is-fetching-properties="isFetching"
				:count-properties="filteredCount"
				@apply-filters="handleSidebarApplyFilter"
				@clear-filters="handleSidebarClearFilters"
				@retry-selected-agent="retrySelectedAgentSummary"
			/>

			<Transition
				:enter-active-class="'transition-opacity duration-300 ease-out'"
				:enter-from-class="'opacity-0'"
				:enter-to-class="'opacity-100'"
				:leave-active-class="'transition-opacity duration-300 ease-in'"
				:leave-from-class="'opacity-100'"
				:leave-to-class="'opacity-0'"
			>
				<div
					v-show="isSidebarActive"
					class="fixed inset-0 z-50 flex h-screen w-screen bg-black/40"
				/>
			</Transition>
		</Teleport>
	</ClientOnly>

	<!--
		On mobile-map the chrome (navbar + filter bar) is a single fixed
		container of 125px total. Padding-top reserves that space for the
		in-flow content below. We do NOT set `h-dvh` on mobile (only
		`lg:h-dvh`) so the sheet + spacer can extend the body beyond the
		viewport — that extra scroll range is what iOS Safari needs to
		retract the URL bar on user gesture-scroll.
	-->
	<div
		:style="pageContentStyle"
		:class="{
			'pointer-events-none': isSidebarActive
		}"
		class="relative w-full pt-[125px] lg:h-dvh lg:pt-[83px]"
	>
		<ClientOnly>
			<MobileScrollToMapButton
				v-if="propertyListSectionRef"
				:filters="filters"
				:is-menu-open="
					menuBottomLevel === SheetLevel.CLOSED && !isPropertyListFooterVisible
				"
				:scroll-element="propertyListSectionRef"
				:is-view-more-button-in-viewport="isViewMoreButtonInViewport"
				:view-more-button-position="viewMoreButtonPositionComputation"
				:hide-after="mobileScrollButtonHideAfterElement"
				@open-map-vision="handleOpenMapFromMobileButton"
			/>
		</ClientOnly>

		<div class="size-full divide-y divide-white-dark-1 lg:bg-white">
			<div class="size-full lg:bg-black">
				<LazyContainerFluid
					main-class="block lg:grid lg:grid-cols-[minmax(0,45%)_1fr] xl:grid-cols-[minmax(0,60%)_1fr] 2xl:grid-cols-[minmax(0,820px)_1fr] lg:h-full"
					class="lg:bg-white lg:h-full"
				>
					<BottomSheetScaffold
						ref="bottomSheetScaffoldRef"
						:snap-heights="snapHeights"
						:top-chrome-height="MOBILE_CHROME_HEIGHT"
						:closed-top-chrome-height="FILTER_BAR_HEIGHT"
						:is-cta-enabled="isFetching || filteredCount > 0"
						:is-footer-visible="isPropertyListFooterVisible"
						sheet-class="bottom-sheet z-20 bg-white lg:static lg:h-[calc(100dvh-83px)] lg:cursor-auto! lg:pt-0"
					>
						<template #content="sheet">
							<LazyPropertyListSection
								ref="propertyListSectionRef"
								:class="[sectionHeightClass]"
								:is-user-dragging="sheet.isDragging"
								:is-fetching-for-the-first-time="isFetchingForTheFirstTime"
								:has-properties="hasProperties"
								:property-count-label="propertyCountLabel"
								:info-label="infoLabel"
								:selected-agent="selectedAgent"
								:is-fetching-selected-agent="isFetchingSelectedAgent"
								:filters="filters"
								:is-fetching="isFetching"
								:properties="properties"
								should-show-shorts-section
								:shorts-display-mode="shortsDisplayMode"
								:use-shorts-cards-on-mobile="isShortsSrpVariant"
								:shorts-info-label="infoLabel"
								:should-show-next-page-button="shouldShowNextPageButton"
								:tracking-context="trackingContext"
								:empty-state-data="emptyStateData"
								:breadcrumb-items="breadcrumbItems"
								:menu-bottom-level="sheet.menuBottomLevel"
								:current-y="sheet.currentY"
								:is-dragging="sheet.isDragging"
								:is-transitioning="sheet.isTransitioning"
								favorite-button-class="z-30 lg:z-40"
								@update-ordenation="updateOrdenation"
								@reorder-clicked="handleReorderClicked"
								@next-page="nextPage"
								@breadcrumb-clicked="handleBreadcrumbClicked"
								@region-clicked="handleRegionClicked"
								@property-card-hover="handleCardHover"
								@property-card-unhover="handleCardUnhover"
								@filter-link-clicked="handleFilterLinkClicked"
								@view-more-button-visibility-changed="
									handleViewMoreButtonVisibilityChanged
								"
								@view-more-button-position-changed="
									handleViewMoreButtonPositionChanged
								"
								@footer-visibility-changed="handleFooterVisibilityChanged"
							/>
						</template>

						<template #cta>
							<div class="flex items-center">
								<IconMaterialSymbolsLightChevronLeft
									class="mr-2 size-6 rotate-90"
								/>
								<div
									v-if="isFetching"
									class="h-[18px] w-[210px] animate-pulse rounded bg-white-dark-3"
								/>
								<span v-else class="text-sm font-medium">
									{{ mobilePropertyCountLabel }}
								</span>
							</div>
						</template>
					</BottomSheetScaffold>
					<section
						id="map-section"
						data-listing-map-controls-offset
						:class="[
							'fixed inset-x-0 bottom-0 top-[65px] z-0 w-full lg:relative lg:inset-auto lg:top-auto lg:z-auto lg:h-[calc(100dvh-163px)] transition-opacity duration-300',
							isMapVisuallyHidden
								? 'opacity-0 pointer-events-none'
								: 'opacity-100 pointer-events-auto'
						]"
					>
						<ClientOnly fallback-tag="div">
							<template #fallback>
								<div
									class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
								></div>
							</template>
							<div
								class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
							>
								<!--
									Sem `map-id` de propósito: herda o default 'portal-map' do
									GmapMap — um mapId válido, usado por todos os outros mapas do
									site. Um mapId válido é obrigatório para os AdvancedMarkers
									(pin de POI) e evita o mapa azul. O mapId anterior
									("'portal-listing-map'", com aspas literais) era inválido e
									quebrava o mapa. NÃO reintroduza um mapId custom sem registrá-lo
									no Google Cloud — ver
									core/service/googleMaps/MAP_STYLING_MAPID_ISSUE.md.
								-->
								<GmapMap
									v-if="isMapEnabled"
									id="portal-listing-map"
									ref="gmapRef"
									:center="mapState.mapConfig.center"
									:bounds="mapState.mapConfig.bounds"
									:zoom="mapState.mapConfig.zoom"
									:min-zoom="mapState.mapConfig.minZoom"
									:max-zoom="mapState.mapConfig.maxZoom"
									:options="mapOptions"
									:clickable-icons="false"
									:class="{
										'invisible pointer-events-none':
											mapController.state.isInitializing.value
									}"
									class="relative size-full h-full! touch-none"
									@drag-start="handleDragStart"
									@drag-end="handleDragEnd"
									@zoom-changed="handleZoomChanged"
								>
									<GmapClusterMarker
										v-for="cluster in readyClusters"
										:key="getClusterKey(cluster.geometry.id)"
										:ref="(el: Element | ComponentPublicInstance | null) => setClusterMarkerRef(cluster.geometry.id, el)"
										:min-zoom="mapState.mapConfig.minZoom"
										:max-zoom="mapState.mapConfig.maxZoom"
										:cluster="cluster"
										:map="gmapRef?.mapInstance"
										:selected-from-parent="
											selectedClustersData.has(cluster.geometry.id)
										"
										:drawing-highlighted="
											!selectedClustersData.has(cluster.geometry.id) &&
											drawingHighlightedClusterIds.has(cluster.geometry.id)
										"
										:cluster-sizes="
											mapController.clusterSizes.value.get(cluster.geometry.id)
										"
										:selected-clusters-count="selectedClustersData.size"
										@cluster-selection-changed="handleClusterSelectionChanged"
										@cluster-deselected-by-resolution-change="
											handleClusterDeselectedByResolution
										"
										@cluster-pan-zoom="handleClusterClicked"
										@clear-selected-clusters-before-zoom="
											handleClearSelectedClusters
										"
									/>
									<GmapMarker
										v-if="markerPosition"
										:position="markerPosition"
										:variant="'red'"
										size="default"
										:address="addressGmapMarker"
										:gmp-clickable="false"
										:gmp-draggable="false"
									/>

									<template #visible>
										<div
											class="pointer-events-none absolute inset-x-4 top-4 z-10 flex flex-wrap items-center justify-end gap-2 lg:inset-x-6 lg:top-6 lg:gap-4 [&_button]:px-3 [&_button]:py-2 lg:[&_button]:px-4"
										>
											<MapDrawingButton
												:is-drawing-mode="isDrawingMode"
												:current-mode="currentDrawingMode"
												class="pointer-events-none [&_button]:pointer-events-auto"
												@toggle="handleDrawingModeToggle"
											/>
											<Button
												v-if="shouldShowSeeAllCityButton"
												variant="white-shadow"
												class="pointer-events-auto size-min h-11 min-w-11 shrink-0"
												size="sm"
												aria-label="Ver toda a cidade"
												title="Toda a cidade"
												type="button"
												@click="handleSeeAllCity"
											>
												<span>Toda a cidade</span>
											</Button>
											<Button
												v-if="selectedClustersData.size > 0"
												variant="primary"
												class="pointer-events-auto size-min h-11 min-w-11 shrink-0"
												size="sm"
												:aria-label="`Limpar seleção (${selectedClustersData.size})`"
												title="Limpar"
												type="button"
												@click="handleClearSelectedClusters"
											>
												<span>Limpar</span>
												<span
													aria-hidden="true"
													class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-light-3 px-1 tabular-nums"
												>
													{{ selectedClustersData.size }}
												</span>
											</Button>
										</div>

										<!-- Drawing mode indicator -->
										<Transition
											enter-active-class="transition-opacity duration-200"
											enter-from-class="opacity-0"
											enter-to-class="opacity-100"
											leave-active-class="transition-opacity duration-200"
											leave-from-class="opacity-100"
											leave-to-class="opacity-0"
										>
											<div
												v-if="isDrawingMode"
												:style="drawingIndicatorStyle"
												class="pointer-events-none absolute inset-x-4 bottom-6 z-10 flex justify-center md:inset-x-6"
											>
												<div
													class="max-w-full rounded-lg bg-primary px-4 py-2 text-center text-sm font-medium text-white shadow-lg"
												>
													{{
														isDrawing
															? 'Desenhando...'
															: isMobileScreen
																? 'Toque e arraste para desenhar a área'
																: 'Clique e arraste para desenhar a área'
													}}
												</div>
											</div>
										</Transition>
									</template>
								</GmapMap>
							</div>
						</ClientOnly>
					</section>
				</LazyContainerFluid>
			</div>
		</div>
	</div>
</template>
